import { useId, useState } from 'react';

import type { Supplier } from '@/shared/tariffs/types';
import { useCreateSupplier, useUpdateSupplier } from '@/shared/tariffs/useTariffs';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input } from '@/shared/ui/Input';

/** `bosch.es, ventas@makita.es  heco.de` → three domains. The back cleans them again. */
function splitDomains(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((each) => each.trim())
    .filter(Boolean);
}

/**
 * A supplier, new or changed: the name, the domains its mail comes from (what «Cargar
 * como tarifa» and later the triage recognise it by), and notes. Only for whoever manages
 * the documents; the API refuses the rest anyway.
 */
export function SupplierDialog({
  supplier,
  onDone,
}: {
  supplier?: Supplier;
  onDone: (saved?: Supplier) => void;
}) {
  const id = useId();
  const create = useCreateSupplier();
  const update = useUpdateSupplier(supplier?.id ?? '');
  const mutation = supplier ? update : create;
  const [name, setName] = useState(supplier?.name ?? '');
  const [domains, setDomains] = useState((supplier?.domains ?? []).join(', '));
  const [notes, setNotes] = useState(supplier?.notes ?? '');

  function submit() {
    const payload = {
      name: name.trim(),
      domains: splitDomains(domains),
      notes: notes.trim() || null,
    };
    mutation.mutate(payload, { onSuccess: (saved) => onDone(saved) });
  }

  return (
    <Dialog
      title={supplier ? `Editar ${supplier.name}` : 'Nuevo proveedor'}
      onClose={() => onDone()}
      footer={
        <>
          <Button variant="secondary" onClick={() => onDone()} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={!name.trim() || mutation.isPending}>
            {mutation.isPending ? 'Guardando…' : supplier ? 'Guardar' : 'Crear proveedor'}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) submit();
        }}
      >
        <Input
          label="Nombre"
          value={name}
          maxLength={200}
          onChange={(event) => setName(event.target.value)}
        />
        <Input
          label="Dominios de su correo"
          optional
          value={domains}
          onChange={(event) => setDomains(event.target.value)}
          placeholder="bosch.es, bosch-professional.com"
          hint="Separados por comas. Sirve para reconocer sus correos al cargar una tarifa."
        />
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-notes`} className="text-ink-2 text-[13.5px] font-medium">
            Notas <span className="text-ink-4 font-normal">(opcional)</span>
          </label>
          <textarea
            id={`${id}-notes`}
            className="field h-auto min-h-24 py-2"
            rows={4}
            maxLength={4000}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="El comercial, el pedido mínimo, cómo se le pide…"
          />
        </div>
        {mutation.isError ? (
          <ErrorMessage error={mutation.error}>No se pudo guardar el proveedor</ErrorMessage>
        ) : null}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
